import { useState, type FormEvent } from 'react';
import { apiPost, describeError } from '../../api/client';
import styles from '../peer/PeerAssessmentActions.module.css';

/**
 * Withdrawing your own direct-report rating, from
 * `DirectReportAssessmentPage`. Mirrors `PeerWithdrawalForm` exactly: a
 * required reason as the confirmation surface itself, working from the one
 * record `/direct-report-assessments` already names (`ownAssessmentId`)
 * rather than a fetched record list.
 */
export function DirectReportWithdrawalForm({
  employeeId,
  attributeId,
  attributeName,
  assessmentId,
  currentRating,
  onWithdrawn,
  onClose,
}: {
  employeeId: string;
  attributeId: string;
  attributeName: string;
  assessmentId: string;
  currentRating: number;
  /** Re-reads `/direct-report-assessments` — the withdrawn attribute
   * reappears as not yet rated, from the same authoritative source as
   * everything else on the page. */
  onWithdrawn: () => void;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const [state, setState] = useState<{ status: 'editing' | 'submitting' } | { status: 'failed'; message: string }>({
    status: 'editing',
  });
  const submitting = state.status === 'submitting';
  const reasonIsValid = reason.trim().length > 0;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (submitting || !reasonIsValid) return;

    setState({ status: 'submitting' });
    try {
      await apiPost(`/employees/${employeeId}/attributes/${attributeId}/assessments/${assessmentId}/withdrawal`, {
        reason: reason.trim(),
      });
      onWithdrawn();
    } catch (error) {
      setState({ status: 'failed', message: describeError(error) });
    }
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit} aria-label={`Withdraw your rating of ${attributeName}`}>
      <p className={styles.confirm}>
        Withdraw your rating of <strong>{currentRating} out of 20</strong> for {attributeName}?
      </p>
      <p className={styles.note}>
        The rating will stop counting towards anything derived from it. It is not deleted: it is kept, marked as
        withdrawn, with the reason you give. This cannot be undone.
      </p>

      <div className={styles.field}>
        <label htmlFor={`dr-withdrawal-reason-${assessmentId}`} className={styles.label}>
          Reason <span className={styles.required}>required</span>
        </label>
        <textarea
          id={`dr-withdrawal-reason-${assessmentId}`}
          className={styles.textarea}
          rows={2}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          disabled={submitting}
          placeholder="Why this rating should no longer stand"
          maxLength={2000}
        />
      </div>

      {state.status === 'failed' ? (
        <p className={styles.error} role="alert">
          {state.message}
        </p>
      ) : null}

      <div className={styles.formActions}>
        <button type="submit" className={styles.destructive} disabled={submitting || !reasonIsValid}>
          {submitting ? 'Withdrawing…' : 'Withdraw rating'}
        </button>
        <button type="button" className={styles.secondary} onClick={onClose} disabled={submitting}>
          Cancel
        </button>
      </div>
    </form>
  );
}
